import {
  DateFormat,
  NullableDateInput,
  stringifyDate,
} from '@superdispatch/dates';
import { CUSTOMER_TYPES, VEHICLE_TYPES } from '@superdispatch/sdk';
import { PricingInsightsDTO } from 'shared/modules/pricing-insights/PricingInsightsDTO';
import { verificationSteps } from 'shared/modules/superpay/SuperPayDTO';
import { BUSINESS_HOURS_TYPES } from 'shared/utils/businessHoursDisplay';
import { businessHoursRowSchema } from 'shared/data/BusinessHoursDTO';
import { z } from 'zod';
import { OFFER_SOURCES } from '../../core/OfferSourceEnum';
import { OFFER_STATUSES } from '../../core/OffersStatusEnum';

const zodString = z
  .string()
  .nullish()
  .transform((value) => value ?? '');

const zodBoolean = z.preprocess(
  (value) =>
    typeof value === 'string' || typeof value === 'number'
      ? /^(true|1)$/i.test(String(value))
      : value,
  z.boolean().nullable().default(false).catch(false),
);

const zodNumber = z.preprocess(
  // `Number('')` is `0`, which would turn a cleared numeric input into a value.
  (value) => (value == null || value === '' ? null : Number(value)),
  z.number().nullable().default(null).catch(null),
);

/**
 * Same as {@link zodNumber}, but additionally parses numeric strings with
 * thousand separators (e.g. `"1,234.5"`).
 */
const zodFloat = z.preprocess((value) => {
  if (typeof value === 'string') {
    const parsed = parseFloat(value.replace(/,/g, ''));
    return Number.isFinite(parsed) ? parsed : null;
  }

  return value;
}, zodNumber);

function zodDateString(format: DateFormat) {
  return z.preprocess(
    (value) => stringifyDate(value as NullableDateInput, { format }),
    z.string().nullable().default(null),
  );
}

const publicOfferOrderDriverSchema = z.object({
  guid: zodString,
  name: zodString,
  email: zodString,
});

export type PublicOfferOrderVehicleDTO = z.infer<
  typeof publicOfferOrderVehicleSchema
>;
const publicOfferOrderVehicleSchema = z.object({
  year: zodFloat,
  make: zodString,
  model: zodString,
  type: z.enum(VEHICLE_TYPES).nullable().catch(null).default(null),

  is_inoperable: zodBoolean,
  requires_enclosed_trailer: zodBoolean,

  curb_weight: zodFloat,
  curb_weight_unit: zodString,

  height: zodFloat,
  length: zodFloat,
  width: zodFloat,

  sample_photo: z
    .object({
      guid: zodString,
      photo_url: zodString,
      thumbnail_url: zodString,
    })
    .nullable()
    .prefault({}),
});

const publicOfferUserSchema = z.object({
  guid: zodString,
  hash: zodString,
  token: zodString,
  is_paying_carrier: zodBoolean,
  company_type: z
    .enum(['owner_operator', 'owner_operator_pro', 'fleet'])
    .nullable()
    .catch(null)
    .default(null),
});

export type PublicOfferDTO = z.infer<typeof publicOfferSchema>;
export const publicOfferSchema = z.object({
  uuid: zodString,
  guid: zodString,
  terms_and_conditions: zodString,
  user: publicOfferUserSchema.nullable().default(null),
  created_at: zodDateString('DateTimeISO'),
  status_changed_at: zodDateString('DateTimeISO'),
  carrier_guid: zodString,
  carrier_name: zodString,
  carrier_contact_name: zodString,

  is_first_offer: zodBoolean,
  is_counter_offer: zodBoolean,
  is_price_negotiation: zodBoolean,

  bid_price: zodFloat,
  original_price: zodFloat,
  status: z.enum(OFFER_STATUSES).catch('pending'),
  source: z.enum(OFFER_SOURCES).nullable().catch(null).default(null),
  order: z.object({
    number: zodString,
    price: zodFloat,
    broker_fee: zodFloat,
    payment_method_display: zodString,
    payment_terms_display: zodString,
    pickup_city: zodString,
    pickup_state: zodString,
    pickup_zip: zodString,
    pickup_date_type: zodString,
    pickup_business_type: z
      .enum(CUSTOMER_TYPES)
      .nullable()
      .catch(null)
      .default(null),
    pickup_business_hours_type: z
      .enum(BUSINESS_HOURS_TYPES)
      .nullable()
      .catch(null)
      .default(null),
    pickup_business_hours: z.array(businessHoursRowSchema).prefault([]),
    pickup_date: zodDateString('DateTimeISO'),
    delivery_city: zodString,
    delivery_state: zodString,
    delivery_zip: zodString,
    delivery_date_type: zodString,
    delivery_business_type: z
      .enum(CUSTOMER_TYPES)
      .nullable()
      .catch(null)
      .default(null),
    delivery_business_hours_type: z
      .enum(BUSINESS_HOURS_TYPES)
      .nullable()
      .catch(null)
      .default(null),
    delivery_business_hours: z.array(businessHoursRowSchema).prefault([]),
    delivery_date: zodDateString('DateTimeISO'),
    instructions: zodString,
    broker_name: zodString,
    broker_guid: zodString,
    broker_phone: zodString,
    shipper_order_guid: zodString,
    driver: publicOfferOrderDriverSchema.nullable().prefault({}),
    vehicles: z.array(publicOfferOrderVehicleSchema).prefault([]),
    price_per_mile: zodFloat,
    distance_miles: zodFloat,
    shipper_profile_url: zodString,
  }),
  superpay: z.object({
    is_verification_available: z.boolean().catch(false).default(false),
    verification: z.enum(verificationSteps).catch('restricted'),
  }),
});

export type AcceptPublicOfferResponseDTO = z.infer<
  typeof acceptPublicOfferResponseSchema
>;
export const acceptPublicOfferResponseSchema = z.object({
  is_paying_carrier: zodBoolean,
  broker_order_guid: zodString,
  carrier_guid: zodString,
  order_guid: zodString,
  order_url: zodString,
  token: zodString,
  user_guid: zodString,
});

export type PublicOfferDriverDTO = z.infer<typeof publicOfferDriverSchema>;
export const publicOfferDriverSchema = z.object({
  guid: zodString,
  name: zodString,
  email: zodString,
  inbox: zodString,
  phone: zodString,
  notes: zodString,

  is_myself: zodBoolean,
  is_activated: zodBoolean,
  is_suspended: zodBoolean,

  trailer: z.object({ capacity: zodFloat }).nullable().prefault({}),

  drivers_licence: z.object({ state: zodString, number: zodString }),
});

export type PublicOfferAddDriverDTO = z.infer<
  typeof publicOfferAddDriverSchema
>;
const publicOfferAddDriverSchema = z.object({
  name: zodString,
  email: zodString,
  phone: zodString,
  is_myself: zodBoolean,
});

export type PublicOfferAssignResponseDTO = z.infer<
  typeof publicOfferAssignResponseSchema
>;
export const publicOfferAssignResponseSchema = z.object({
  autologin_link: zodString,
});

const publicOfferDeclineReasons = [
  'low_price',
  'lack_of_capacity',
  'inconvenient_dates',
] as const;

export type PublicOfferDeclinePayloadDTO = z.infer<
  typeof publicOfferDeclinePayloadSchema
>;
export const publicOfferDeclinePayloadSchema = z.object({
  decline_comment: z
    .string()
    .max(512, 'Maximum length is 512 characters')
    .nullish()
    .transform((value) => value ?? ''),
  decline_reasons: z.array(z.enum(publicOfferDeclineReasons)).prefault([]),
});

export function mapPublicOfferLoadToPricingInsights(
  order: PublicOfferDTO['order'],
): PricingInsightsDTO {
  return {
    origin: {
      zip: order.pickup_zip,
      city: order.pickup_city,
      state: order.pickup_state,
    },

    destination: {
      zip: order.delivery_zip,
      city: order.delivery_city,
      state: order.delivery_state,
    },

    vehicles: order.vehicles.map((vehicle) => ({
      year: vehicle.year,
      type: vehicle.type,
      make: vehicle.make,
      model: vehicle.model,
      is_inoperable: vehicle.is_inoperable,
    })),
  };
}

export type PublicOfferInviteDriverDTO = z.infer<
  typeof publicOfferInviteDriverSchema
>;
export const publicOfferInviteDriverSchema = z.object({
  email: z
    .string()
    .min(1, 'This field is required')
    .refine(
      (value) => !value || z.email().safeParse(value).success,
      'Enter a valid email',
    )
    .prefault(''),
  phone_number: z.string().min(1, 'This field is required').prefault(''),
  name: zodString,
});

export type DriverType = z.infer<typeof driverSchema>;
const driverSchema = z.object({
  guid: zodString,
  email: zodString,
  phone: zodString,
  name: zodString,
  inbox: zodString,
  trailer: z.object({ capacity: zodFloat }),
  drivers_licence: z.object({ number: zodString, state: zodString }),
  is_activated: z.boolean().catch(false).default(false),
  is_suspended: z.boolean().catch(false).default(false),
  is_myself: z.boolean().catch(false).default(false),
});

export type PublicOfferInviteResponseDTO = z.infer<
  typeof publicDriverInviteResponseSchema
>;
export const publicDriverInviteResponseSchema = driverSchema.extend({
  driver: driverSchema,
});

export type PublicDriverAcceptDriverResponseDTO = z.infer<
  typeof publicDriverAcceptResponseSchema
>;
export const publicDriverAcceptResponseSchema = driverSchema.extend({
  driver: driverSchema,
});
